<template>
  <v-card outlined class="rounded-lg ordera-box-shadow" flat>
    <v-card-title class="font-weight-bold">
      <v-avatar color="primary" size="36" class="mr-3">
        <v-icon color="white">mdi-bullhorn-outline</v-icon>
      </v-avatar>
      {{ $t("outage_management") }}
    </v-card-title>

    <v-card-text>
      <p class="text-body-2 grey--text text--darken-1">{{ $t("outage_create_hint") }}</p>

      <v-form ref="form" v-model="valid" @submit.prevent="submit">
        <div class="d-flex flex-wrap mb-1">
          <v-chip
            v-for="lang in availableLanguages"
            :key="lang.code"
            :color="activeLang === lang.code ? 'primary' : 'grey lighten-3'"
            :text-color="activeLang === lang.code ? 'white' : undefined"
            class="mr-2 mb-2 font-weight-medium"
            label
            @click="activeLang = lang.code"
          >
            <span v-if="lang.flag" class="mr-1">{{ lang.flag }}</span>
            {{ lang.text }}
            <v-icon v-if="isFilled(lang)" right x-small :color="activeLang === lang.code ? 'white' : 'success'">
              mdi-check-circle
            </v-icon>
          </v-chip>
        </div>
        <div class="caption grey--text mb-4">{{ $t("outage_translations_hint") }}</div>

        <div
          v-for="lang in availableLanguages"
          :key="lang.code"
          v-show="activeLang === lang.code"
        >
          <text-field
            v-if="lang.code === 'default'"
            v-model="newTitle"
            :label="$t('outage_title_label')"
            :rules="titleRules"
            required
          ></text-field>
          <text-field
            v-else
            v-model="translationFor(lang.code).title"
            :label="$t('outage_title_label')"
          ></text-field>

          <text-area
            v-if="lang.code === 'default'"
            v-model="newMessage"
            :label="$t('outage_message_label')"
            :rules="messageRules"
            :hide-details="false"
            rows="4"
            required
          ></text-area>
          <text-area
            v-else
            v-model="translationFor(lang.code).message"
            :label="$t('outage_message_label')"
            :hide-details="false"
            rows="4"
            :rules="translationMessageRules(lang)"
          ></text-area>

          <div class="d-flex justify-end">
            <span
              class="caption"
              :class="messageLength(lang) > 500 ? 'warning--text font-weight-medium' : 'grey--text'"
            >
              {{ messageLength(lang) }} / 500
              <template v-if="messageLength(lang) > 500">
                — {{ $t("outage_message_long_warning") }}
              </template>
            </span>
          </div>
        </div>

        <v-expand-transition>
          <v-alert v-if="activeOutage" type="warning" text dense class="caption mt-3 mb-0">
            {{ $t("outage_replace_warning", { title: activeOutage.title }) }}
          </v-alert>
        </v-expand-transition>

        <v-btn
          type="submit"
          class="primary-gradient white--text mt-4"
          :disabled="!valid || loading"
          :loading="loading"
          block
          x-large
          depressed
        >
          <v-icon left>mdi-send-outline</v-icon>
          {{ $t("publish") }}
        </v-btn>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "OutageForm",
  props: {
    activeOutage: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      activeLang: "default",
      newTitle: "",
      newMessage: "",
      translations: [],
    };
  },
  computed: {
    availableLanguages() {
      const def = [
        {
          code: "default",
          text: "default",
          locale: "default",
          flag: "🇷",
          script: "default",
        },
      ];

      // Obavještenja su globalna (nisu vezana za jelovnik jednog restorana),
      // pa se ovdje uvijek nude svi jezici, bez filtriranja po restaurant.settings.language.
      return def.concat(this.getAvailableLanguages());
    },
    titleRules() {
      return [
        (v) => !!v || this.$t("outage_title_required"),
        (v) => !v || v.length <= 255 || this.$t("outage_title_max_length"),
      ];
    },
    messageRules() {
      return [(v) => !!v || this.$t("outage_message_required")];
    },
  },
  watch: {
    availableLanguages: {
      handler() {
        this.syncTranslations();
      },
      deep: true,
    },
  },
  created() {
    this.syncTranslations();
  },
  methods: {
    syncTranslations() {
      const existing = this.translations || [];
      this.translations = this.availableLanguages.map((lang) => {
        const found = existing.find((t) => t.code === lang.code);
        return found ? { ...found } : { code: lang.code, title: "", message: "" };
      });
    },
    translationFor(code) {
      return this.translations.find((t) => t.code === code) || { title: "", message: "" };
    },
    translationMessageRules(lang) {
      return [
        (v) =>
          !this.translationFor(lang.code).title ||
          !!v ||
          this.$t("outage_translation_message_required"),
      ];
    },
    messageLength(lang) {
      if (lang.code === "default") {
        return (this.newMessage || "").length;
      }
      return (this.translationFor(lang.code).message || "").length;
    },
    isFilled(lang) {
      if (lang.code === "default") return false;
      const t = this.translationFor(lang.code);
      return !!(t.title || t.message);
    },
    submit() {
      if (!this.$refs.form.validate()) return;

      const translations = this.translations
        .filter((t) => t.code !== "default")
        .filter((t) => t.message);

      this.$emit("publish", {
        title: this.newTitle,
        message: this.newMessage,
        translations,
      });
    },
    reset() {
      this.newTitle = "";
      this.newMessage = "";
      this.activeLang = "default";
      this.syncTranslations();
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
  },
};
</script>
